import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SubTabs from "@/components/SubTabs";

const PERIODE_ORDER = ["today", "end_of_week", "next_week", "end_of_month"];

const fmt = (n) =>
  new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);

const fmtNum = (n) =>
  new Intl.NumberFormat("da-DK", { maximumFractionDigits: 1 }).format(n);

function bookingColor(pct) {
  if (pct >= 95) return "#22C55E";
  if (pct >= 85) return "#84CC16";
  if (pct >= 70) return "#EAB308";
  if (pct >= 55) return "#F97316";
  return "#EF4444";
}

function CityRow({ city }) {
  const color = bookingColor(city.booking_pct);
  const barWidth = Math.min(city.booking_pct, 100);

  return (
    <div className="py-3 border-b border-border last:border-0">
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <span className="text-sm font-bold text-foreground">{city.display_name}</span>
        <div className="flex items-center gap-3 text-[13px] text-muted-foreground">
          {city.omsaetning > 0 && (
            <span className="text-foreground/70">{fmt(city.omsaetning)}</span>
          )}
          {city.antal_bookinger > 0 && (
            <span>{city.antal_bookinger} booking{city.antal_bookinger !== 1 ? "er" : ""}</span>
          )}
          <span className="font-bold text-sm" style={{ color }}>
            {city.booking_pct}%
          </span>
        </div>
      </div>

      {/* Booking bar */}
      <div className="relative w-full bg-secondary rounded-full h-2.5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${barWidth}%`, background: color }}
        />
        {/* Threshold markers */}
        {[70, 85, 95].map((t) => (
          <div
            key={t}
            className="absolute top-0 bottom-0 w-px"
            style={{ left: `${t}%`, background: "rgba(128,128,128,0.35)" }}
          />
        ))}
      </div>

      <div className="flex justify-between mt-1 text-[13px] text-muted-foreground">
        <span>{fmtNum(city.timer)} arb.timer</span>
        {city.ledige_timer > 0 && (
          <span>{fmtNum(city.ledige_timer)} ledige timer</span>
        )}
      </div>
    </div>
  );
}

function PeriodeCard({ label, cities }) {
  if (!cities || cities.length === 0) {
    return (
      <Card className="bg-card border-border shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold tracking-tight">{label}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[13px] text-muted-foreground">Ingen data tilgængelig</p>
        </CardContent>
      </Card>
    );
  }

  // Vægtet gennemsnitlig booking pct
  const totalTimer = cities.reduce((a, c) => a + c.timer, 0);
  const weightedAvg = totalTimer > 0
    ? cities.reduce((a, c) => a + c.booking_pct * c.timer, 0) / totalTimer
    : 0;
  const avgColor = bookingColor(weightedAvg);

  return (
    <Card className="bg-card border-border shadow-none">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-bold tracking-tight">{label}</CardTitle>
          <span className="text-lg font-extrabold" style={{ color: avgColor }}>
            {Math.round(weightedAvg)}%
          </span>
        </div>
        <p className="text-[13px] text-muted-foreground">Vægtet bookinggrad</p>
      </CardHeader>
      <CardContent className="pt-0">
        {cities.map((city) => (
          <CityRow key={city.city} city={city} />
        ))}
      </CardContent>
    </Card>
  );
}

const TYPE_OPTIONS = [
  { key: "all", label: "Begge" },
  { key: "kos", label: "Kosmetolog" },
  { key: "fri", label: "Frisør" },
];

export function ForecastView({ data }) {
  const [typeFilter, setTypeFilter] = useState("all");

  if (!data) return null;

  const { periods, periode_labels } = data;

  // Støtter både ny struktur {all, kos, fri} og gammel struktur (array)
  function getCities(periodeKey) {
    const p = periods?.[periodeKey];
    if (!p) return [];
    const list = Array.isArray(p) ? p : (p[typeFilter] || p["all"] || []);
    return [...list].sort((a, b) => a.display_name.localeCompare(b.display_name, "da"));
  }

  return (
    <div className="space-y-6 animate-fade-in-up opacity-0 stagger-1">
      {/* Overskrift "Forecast — Bookinggrad pr. afdeling" fjernet — "Forecast"
          præfikses i stedet på hver boks (se label nedenfor). Kun type-filteret bevares. */}
      {/* Type-filter — fælles undermenu-skabelon (SubTabs). */}
      <SubTabs tabs={TYPE_OPTIONS} value={typeFilter} onChange={setTypeFilter} className="mb-2" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {PERIODE_ORDER.map((key) => (
          <PeriodeCard
            key={key}
            label={`Forecast ${periode_labels?.[key] || key}`}
            cities={getCities(key)}
          />
        ))}
      </div>

      {data.last_updated && (
        <p className="text-[13px] text-muted-foreground text-right">
          Opdateret: {new Date(data.last_updated).toLocaleString("da-DK")}
        </p>
      )}
    </div>
  );
}
